
import { createCDNFileUrl, getMkLogin } from "@/operations";
import dayjs from "dayjs";
import { Component, namespace, Vue } from "nuxt-property-decorator";
import RouterLink from "~/components/atoms/text/RouterLink.vue";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import LightBox from "~/components/molecules/display/LightBox.vue";
import CompanyDisclaimer from "~/components/organisms/display/dealerProfile/CompanyDisclaimer.vue";
import DeliveryOptionsCard from "~/components/organisms/display/dealerProfile/DeliveryOptionsCard.vue";
import DeliveryOptionsCardMobile from "~/components/organisms/display/dealerProfile/DeliveryOptionsCardMobile.vue";
import ProfileIntroCard from "~/components/organisms/display/dealerProfile/ProfileIntroCard.vue";
import ProfileLayout from "~/components/organisms/display/dealerProfile/ProfileLayout.vue";
import agencyProfileDefault from "~/defaults/profile/agencyProfile";
import legacyDealerSlug from "~/middleware/legacyDealerSlug";
import { createDeliveryOptionsForDisplay } from "~/operations/delivery-options.helper";
import {
  generateAgencyJsonLd,
  generateBreadcrumbJsonLd,
} from "~/operations/jsonld-helper";
import { ButtonText } from "~/operations/messages";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { UserCommentsState } from "~/store/partnerUserComments";
import { AgencyProfile } from "~/types/Profile";

const UserCommentsModule = namespace("partnerUserComments");
const EsyoilModule = namespace("esyoil");

@Component({
  middleware: [legacyDealerSlug],
  components: {
    BreadCrumb,
    CompanyDisclaimer,
    DeliveryOptionsCard,
    DeliveryOptionsCardMobile,
    LightBox,
    ProfileIntroCard,
    ProfileLayout,
    RouterLink,
    Address: () => import("~/components/molecules/text/Address.vue"),
    AgencyCardList: () =>
      import("~/components/organisms/display/dealerProfile/AgencyCardList.vue"),
    AgencyDeliveryArea: () =>
      import(
        "~/components/organisms/display/dealerProfile/AgencyDeliveryArea.vue"
      ),
    DynamicMap: () => import("~/components/molecules/display/DynamicMap.vue"),
    GalleryCard: () => import("~/components/molecules/display/GalleryCard.vue"),
    UserComments: () => import("~/components/organisms/text/UserComments.vue"),
    UserRatingCollection: () =>
      import("~/components/organisms/text/UserRatingCollection.vue"),
  },
})
export default class AgencyProfileView extends Vue {
  agencyProfile: AgencyProfile = agencyProfileDefault;

  @UserCommentsModule.State("userComments")
  userComments: UserCommentsState;

  @UserCommentsModule.Action("loadComments")
  loadUserComments: any;

  @UserCommentsModule.Mutation("clearComments")
  clearUserComments: any;

  @EsyoilModule.Action("dispatchIsEsyoilAccessLoading")
  dispatchIsEsyoilAccessLoading!: any;

  @EsyoilModule.State("isEsyoilAccess")
  isEsyoilAccess!: boolean;

  ButtonText = ButtonText;
  showModal = false;

  layout(cxt) {
    return cxt.route.query?.preview ? "empty" : "default";
  }

  head() {
    const {
      companyProfile,
      address,
      slug,
      orderContactEmail,
      orderContactPhone,
      rating,
    } = this.agencyProfile;

    if (!address || !companyProfile) {
      return {};
    }

    const title = `${this.displayName} Heizöl ${address.city}: Heizölhändler mit Bewertung`;
    const content = `Alle Informationen zum Heizölhändler ${this.displayName} in ${address.postalCode} ${address.city} ✔️Vorstellung ✔️Bewertungen ✔️Kommentare ✔️Kontaktdaten`;
    const robots =
      companyProfile.isTesting || this.$route.query?.preview
        ? "noindex,nofollow,noodp"
        : "index,follow,noodp";
    return {
      ...generateMetaTags(this.$route.path, "partner", [
        { k: "title", v: title },
        { k: "content", v: content },
        { k: "robots", v: robots },
        { k: "keywords", v: `${this.displayName} ${address.city}` },
      ]),
      link: [
        {
          rel: "canonical",
          href: `/heizoelhaendler/${companyProfile.slug}/${this.agencyProfile.slug}`,
        },
      ],
      script: [
        {
          type: "application/ld+json",
          json: [
            generateAgencyJsonLd({
              name: this.displayName,
              urlPath: `/heizoelhaendler/${companyProfile.slug}/${slug}`,
              logoImageUrl: this.logoImage.url,
              address: address,
              contact: {
                email: orderContactEmail,
                telephone: orderContactPhone,
              },
              rating: {
                count: rating.overall.count,
                value: rating.lastYear.average,
                userComments: this.userComments.data,
              },
            }),
            generateBreadcrumbJsonLd(this.breadCrumbs),
          ],
        },
      ],
    };
  }

  async asyncData({ params, env, error, $axios }) {
    try {
      const response = await $axios.get(
        `${env.BACKBONE_ESYOIL}${env.AGENCY_PROFILE}/${params.id}`,
      );
      return { agencyProfile: response.data as AgencyProfile };
    } catch {
      error({ statusCode: 404 });
    }
  }

  async fetch() {
    await this.loadUserComments({
      page: 1,
      body: {
        end: dayjs.utc().format(),
        start: dayjs.utc().subtract(1, "year").format(),
        sortBy: "new",
        sortDesc: true,
        businessCustomerId: this.agencyProfile.id,
      },
      clear: true,
    });
  }

  mounted() {
    window["gtag_enable_tcf_support"] = true;
    this.dispatchIsEsyoilAccessLoading();
  }

  beforeDestroy() {
    this.clearUserComments();
  }

  redirectToLogin() {
    window.location.href = this._getMkLogin;
  }

  get breadCrumbs() {
    const { companyProfile } = this.agencyProfile;
    if (!companyProfile) {
      return [];
    }

    return [
      {
        name: "Heizölhändler",
        linkUrl: "/heizoelhaendler",
      },
      {
        name: companyProfile.profileName,
        linkUrl: `/heizoelhaendler/${companyProfile.slug}`,
      },
      {
        name: `${this.displayName}, ${this.agencyProfile.address.city}`,
        linkUrl: `/heizoelhaendler/${companyProfile.slug}/${this.agencyProfile.slug}`,
      },
    ];
  }

  get displayName() {
    return this.agencyProfile.brandName ?? this.agencyProfile.legalName;
  }

  get logoImage() {
    let imageName = this.displayName;
    if (this.agencyProfile.address) {
      imageName += `, ${this.agencyProfile.address.postalCode} ${this.agencyProfile.address.city}`;
    }
    return {
      url: createCDNFileUrl(
        this.agencyProfile.companyProfile?.media.logoImageUrls.large,
      ),
      name: imageName,
    };
  }

  get mainProfileImage() {
    if (!this.agencyProfile.companyProfile) {
      return undefined;
    }

    const { profileImages } = this.agencyProfile.companyProfile.media;
    return profileImages?.length > 0
      ? {
          url: createCDNFileUrl(profileImages[0]),
          name: `${this.displayName} Profilbild 1`,
        }
      : undefined;
  }

  get additionalProfileImages() {
    if (!this.agencyProfile.companyProfile) {
      return undefined;
    }

    const { profileImages } = this.agencyProfile?.companyProfile?.media;
    if (profileImages?.length > 1) {
      return profileImages.slice(1).map((imageName, index) => ({
        url: createCDNFileUrl(imageName),
        name: `${this.displayName} Profilbild ${index + 1}`,
      }));
    }

    return undefined;
  }

  get deliveryOptions() {
    return createDeliveryOptionsForDisplay(this.agencyProfile.deliveryOptions);
  }

  get agbFileUrl() {
    return createCDNFileUrl(
      this.agencyProfile.companyProfile?.media.agbFileUrl,
    );
  }

  get withdrawalFileUrl() {
    return createCDNFileUrl(
      this.agencyProfile.companyProfile?.media.withdrawalFileUrl,
    );
  }

  get hasEnoughRatings() {
    return this.agencyProfile.rating.overall.count > 5;
  }

  get otherAgencyProfiles() {
    const others = this.agencyProfile.companyProfile?.agencyProfiles.filter(
      (agency) => agency.id !== this.agencyProfile.id,
    );
    return [...others].sort((a, b) =>
      a.address.city.localeCompare(b.address.city),
    );
  }

  get _getMkLogin() {
    return getMkLogin();
  }
}
